import { c as _c } from "react/compiler-runtime";
import { Box, Paper, Stack, Typography } from '@mui/material';
import { alpha, darken, lighten, Theme } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MaintenanceType } from './helpers';
import { renderBannerText } from './bannerText';
export const TYPE_ICONS = {
  [MaintenanceType.Info]: InfoRoundedIcon,
  [MaintenanceType.Success]: CheckCircleRoundedIcon,
  [MaintenanceType.Warning]: ConstructionRoundedIcon,
  [MaintenanceType.Error]: ErrorRoundedIcon
};
export const typeAccent = (type: MaintenanceType) => (theme: Theme) => darken(theme.palette[type].dark, 0.25);
const typeBackground = (type: MaintenanceType) => (theme: Theme) => lighten(theme.palette[type].main, 0.92);
const typeBorder = (type: MaintenanceType) => (theme: Theme) => lighten(theme.palette[type].main, 0.6);
export const bannerShadow = 'rgba(16, 24, 40, 0.08) 0px 4px 16px 0px';
interface BannerCardProps {
  type: MaintenanceType;
  text: string;
  unpublished?: boolean;
  action?: ReactNode;
}
export default function BannerCard(t0) {
  const $ = _c(33);
  const {
    type,
    text,
    unpublished,
    action
  } = t0;
  const {
    t
  } = useTranslation();
  const Icon = TYPE_ICONS[type];
  let t1;
  if ($[0] !== type) {
    t1 = typeAccent(type);
    $[0] = type;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const accent = t1;
  const t2 = `styra.maintenance_type.${type}`;
  let t3;
  if ($[2] !== t || $[3] !== t2) {
    t3 = t(t2);
    $[2] = t;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== accent || $[6] !== type) {
    t4 = theme => ({
      "--banner-accent": accent(theme),
      borderRadius: "12px",
      border: `1px solid ${typeBorder(type)(theme)}`,
      bgcolor: typeBackground(type)(theme),
      boxShadow: bannerShadow
    });
    $[5] = accent;
    $[6] = type;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== accent || $[9] !== t || $[10] !== type || $[11] !== unpublished) {
    t5 = unpublished && <Stack direction="row" sx={theme_0 => ({
      alignItems: "center",
      gap: 0.75,
      px: 2,
      py: 0.75,
      borderRadius: "11px 11px 0 0",
      borderBottom: `1px solid ${typeBorder(type)(theme_0)}`,
      bgcolor: alpha(theme_0.palette[type].main, 0.1),
      color: accent(theme_0)
    })}><VisibilityOffRoundedIcon sx={{
        fontSize: 14
      }} /><Typography variant="caption" sx={{
        fontWeight: 500
      }}>{t("styra.status.unpublished")}<Box component="span" sx={{
          fontWeight: 400,
          opacity: 0.85
        }}>{` · ${t("styra.text.unpublished_banner_visibility")}`}</Box></Typography></Stack>;
    $[8] = accent;
    $[9] = t;
    $[10] = type;
    $[11] = unpublished;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      p: 2,
      gap: 1.5,
      alignItems: "center",
      flexWrap: "wrap"
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = `${type}.main`;
  let t8;
  if ($[14] !== t7) {
    t8 = {
      color: t7,
      fontSize: 24
    };
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== Icon || $[17] !== t8) {
    t9 = <Icon sx={t8} />;
    $[16] = Icon;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      flex: 1,
      minWidth: 240
    };
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== text) {
    t11 = renderBannerText(text);
    $[20] = text;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11) {
    t12 = <Typography component="div" sx={t10}>{t11}</Typography>;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== action || $[25] !== t12 || $[26] !== t9) {
    t13 = <Stack direction="row" sx={t6}>{t9}{t12}{action}</Stack>;
    $[24] = action;
    $[25] = t12;
    $[26] = t9;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t13 || $[29] !== t3 || $[30] !== t4 || $[31] !== t5) {
    t14 = <Paper role="status" aria-label={t3} sx={t4}>{t5}{t13}</Paper>;
    $[28] = t13;
    $[29] = t3;
    $[30] = t4;
    $[31] = t5;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  return t14;
}